import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEarthAmericas } from '@fortawesome/pro-light-svg-icons/faEarthAmericas';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { COUNTRY_CODES } from 'fym-shared/src/geo/countryCodes';
import { getCountryName } from 'fym-shared/src/geo/getCountryName';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';
import { useShareTokenContext } from '../../contexts/ShareTokenContext';
import { countryParamName } from './consts';
import styles from './SMLCountrySelector.module.scss';

const SMLCountrySelectorComponent: React.FC = () => {
  const intl = useIntl();
  const smlRouter = useSMLRouter();
  const { countryCode } = useSMLCountryParam();
  const { isPublicMode } = useShareTokenContext();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointer = (event: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options = useMemo(
    () =>
      COUNTRY_CODES.map((code) => ({ code, name: getCountryName(code, intl.locale) })).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [intl.locale]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return options;
    }
    return options.filter(({ code, name }) => name.toLowerCase().includes(q) || code.toLowerCase().includes(q));
  }, [options, query]);

  const setCountry = useCallback(
    (code: string | undefined) => {
      smlRouter.setParams({ [countryParamName]: code } as never);
    },
    [smlRouter]
  );

  const handleSelect = useCallback(
    (code: string) => {
      // Single-select: re-picking the active country clears back to worldwide.
      setCountry(code === countryCode ? undefined : code);
      setQuery('');
      setOpen(false);
    },
    [countryCode, setCountry]
  );

  const handleClear = useCallback(() => {
    setCountry(undefined);
    setQuery('');
    setOpen(false);
  }, [setCountry]);

  const buttonLabel =
    countryCode !== undefined
      ? getCountryName(countryCode, intl.locale)
      : intl.formatMessage({ id: 'app.sml.countrySelector.worldwide', defaultMessage: 'Worldwide' });

  return (
    <div className={`${styles.geo} ${open ? styles.open : ''}`} ref={ref}>
      <button
        type="button"
        className={`${styles.geoBtn} ${countryCode !== undefined ? styles.on : ''}`}
        onClick={() => setOpen((value) => !value)}
        disabled={isPublicMode}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: 'app.sml.countrySelector.label', defaultMessage: 'Filter by country' })}
      >
        <FontAwesomeIcon icon={faEarthAmericas} className={styles.geoGlobe} />
        <span className={styles.geoLbl}>{buttonLabel}</span>
        <FontAwesomeIcon icon={faChevronRight} className={styles.cx} />
      </button>
      {open && !isPublicMode ? (
        <div
          className={styles.geoPop}
          role="dialog"
          aria-label={intl.formatMessage({ id: 'app.sml.countrySelector.label', defaultMessage: 'Filter by country' })}
        >
          <div className={styles.geoSearch}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'app.sml.countrySelector.searchPlaceholder',
                defaultMessage: 'Filter countries…',
              })}
            />
          </div>
          <div className={styles.geoList}>
            {filtered.map(({ code, name }) => {
              const isActive = code === countryCode;
              return (
                <button
                  type="button"
                  key={code}
                  className={`${styles.geoItem} ${isActive ? styles.on : ''}`}
                  onClick={() => handleSelect(code)}
                >
                  <span className={styles.geoCheck}>{isActive ? <FontAwesomeIcon icon={faCheck} /> : null}</span>
                  <span className={styles.geoCc}>{code}</span>
                  <span className={styles.geoCn}>{name}</span>
                </button>
              );
            })}
            {filtered.length === 0 ? (
              <div className={styles.geoEmpty}>
                {intl.formatMessage(
                  { id: 'app.sml.countrySelector.empty', defaultMessage: 'No country matches “{query}”.' },
                  { query }
                )}
              </div>
            ) : null}
          </div>
          <div className={styles.geoFoot}>
            <button
              type="button"
              className={styles.geoClear}
              onClick={handleClear}
              disabled={countryCode === undefined}
            >
              {intl.formatMessage({ id: 'app.sml.countrySelector.clear', defaultMessage: 'Clear' })}
            </button>
            <button type="button" className={styles.geoDone} onClick={() => setOpen(false)}>
              {intl.formatMessage({ id: 'app.sml.countrySelector.done', defaultMessage: 'Done' })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const SMLCountrySelector = TypedMemo(SMLCountrySelectorComponent);
